import {
  type FC,
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

import { isEmpty } from 'lodash';
import mixpanel, { type Mixpanel } from 'mixpanel-browser';
import { useLocation } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';

import type { AnalyticsEvent } from '~/analytics';
import type { InquiryEventProps } from '~/analytics/inquiryEvents';
import {
  getAdminPageTitle,
  getMaudPageTitle,
  getProfilePageTitle,
} from '~/analytics/trackPageEvents';
import { getPatientPageTitle } from '~/analytics/trackPatientPageEvents';
import { VITE_MIXPANEL_TOKEN } from '~/lib/constants/env';
import { isTestEnvironment } from '~/lib/util/environment.util';
import { getPreferredFullName } from '~/lib/util/patient.util';
import { getCalendarPageTitle } from '~/analytics/trackCalendarPageEvents';
import type { MessagesEventsProps } from '~/analytics/messagesEvents';
import type { UserEventsProps } from '~/analytics/userEvents';

type AnalyticsValueType =
  | string
  | number
  | boolean
  | (string | number | boolean)[];

type EventProps = {
  [key: string]: AnalyticsValueType | Record<string, AnalyticsValueType>;
};

export type AnalyticsContextType = {
  identifyProvider: (userIdentity: GetProviderByIdResponse) => void;
  resetAnalytics: () => void;
  trackEvent: (
    event: AnalyticsEvent,
    props?: EventProps | InquiryEventProps | MessagesEventsProps,
  ) => void;
};

export const AnalyticsContext = createContext<AnalyticsContextType | undefined>(
  undefined,
);

export const useAnalytics = () => useContext(AnalyticsContext);

interface MixpanelProviderProps {
  children: ReactNode;
}

export const AnalyticsProvider: FC<MixpanelProviderProps> = ({ children }) => {
  const {
    eventAnalytics: isEventAnalyticsEnabled,
    mixpanelHeatmapsPatientChart: isMixpanelHeatmapsPatientChartEnabled,
  } = useFlags<LDFlags>();
  const [mixpanelInstance, setMixpanelInstance] = useState<
    Mixpanel | undefined
  >(undefined);

  const location = useLocation();

  useEffect(() => {
    if (isEmpty(VITE_MIXPANEL_TOKEN)) return;
    /* Once legacy mixpanel-heatmaps-patient-chart is removed,
    we can drop the extra checks and initialize Mixpanel directly with heatmaps enabled,
    and simplify the effect by removing dependency array values. */
    if (isMixpanelHeatmapsPatientChartEnabled === undefined) return;
    if (mixpanelInstance) return;
    mixpanel.init(VITE_MIXPANEL_TOKEN, {
      track_pageview: false,
      ...(isMixpanelHeatmapsPatientChartEnabled && {
        autocapture: true,
        record_heatmap_data: true,
        record_sessions_percent: 0,
      }),
    });
    setMixpanelInstance(mixpanel);
  }, [isMixpanelHeatmapsPatientChartEnabled]);

  useEffect(() => {
    if (mixpanelInstance) {
      const currentPath = location.pathname;
      const isAdminPage = currentPath.startsWith('/admin/');
      const isMaudTopLevelPage =
        /\/maud\/(patients|inquiries|tasks|calendar\/schedule)$/.test(
          currentPath,
        );
      const isProfilePage = /^\/maud\/profile\/[^/]+\/[^/]+$/.test(currentPath);
      const isCalendarPage = /^\/maud\/calendar\/([^/]+)/.test(currentPath);
      let pageTitle: string;

      if (isAdminPage) {
        pageTitle = getAdminPageTitle(currentPath);
      } else if (isMaudTopLevelPage) {
        pageTitle = getMaudPageTitle(currentPath);
      } else if (isProfilePage) {
        pageTitle = getProfilePageTitle(currentPath);
      } else if (isCalendarPage) {
        pageTitle = getCalendarPageTitle(currentPath);
      } else {
        pageTitle = getPatientPageTitle(currentPath);
      }
      if (pageTitle) {
        mixpanelInstance?.track_pageview(
          { page: pageTitle },
          { event_name: pageTitle },
        );
      }
    }
  }, [location, mixpanelInstance]);

  useEffect(() => {
    if (!mixpanelInstance || !isMixpanelHeatmapsPatientChartEnabled) {
      return undefined;
    }

    const currentPath = location.pathname;
    const heatmapRoutes = [/^\/maud\/patients\/[^/]+(?:\/.*)?$/];

    const shouldRecord = heatmapRoutes.some((routeRegex) => {
      return routeRegex.test(currentPath);
    });

    if (shouldRecord) {
      mixpanelInstance.start_session_recording();
    } else {
      mixpanelInstance.stop_session_recording();
    }

    return () => {
      mixpanelInstance.stop_session_recording();
    };
  }, [
    location.pathname,
    mixpanelInstance,
    isMixpanelHeatmapsPatientChartEnabled,
  ]);

  const trackEvent = (
    event: AnalyticsEvent,
    props?:
      | EventProps
      | InquiryEventProps
      | MessagesEventsProps
      | UserEventsProps,
  ) => {
    if (isEventAnalyticsEnabled || isTestEnvironment) {
      mixpanelInstance?.track(event, props);
    }
  };

  const identifyProvider = (userIdentity: GetProviderByIdResponse) => {
    const {
      userId,
      providerTypeName,
      email,
      firstName,
      lastName,
      chosenName,
      region,
      hoursPerWeek,
      timeZoneDetails,
      appAccess,
    } = userIdentity;
    mixpanelInstance?.identify(userId);
    mixpanelInstance?.people.set_once({
      'App Access': appAccess,
      'Hours Per Week': hoursPerWeek,
      'Maud Region': region,
      'Maud Timezone': timeZoneDetails?.id,
      'Provider Type Name': providerTypeName,
      $email: email,
      $name: getPreferredFullName(chosenName, firstName, lastName),
    });
  };

  const resetAnalytics = () => {
    mixpanelInstance?.reset();
  };

  const analytics = {
    identifyProvider,
    resetAnalytics,
    trackEvent,
  };

  return (
    <AnalyticsContext.Provider value={analytics}>
      {children}
    </AnalyticsContext.Provider>
  );
};
